import React, { useState } from "react";
import { Check, EyeOff, Trash2, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { imageVariant } from "@/lib/mediaUrl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const ROLE_LABELS = { super_admin: "Super admin", manager: "Gestionnaire", moderator: "Modérateur", associate: "Associé", support: "Support" };
const ADMIN_ROLE_ORDER = ["super_admin", "manager", "moderator", "associate", "support"];
const fmtDateTime = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };

const Actor = ({ user, testId }) => {
  const role = ADMIN_ROLE_ORDER.find((r) => (user?.roles || []).includes(r));
  return (
    <span className="inline-flex items-center gap-1.5 font-semibold" data-testid={testId}>
      {user?.avatar_url
        ? <img src={imageVariant(user.avatar_url, 32)} alt="" className="h-5 w-5 rounded-full object-cover" />
        : <span className="h-5 w-5 rounded-full grid place-items-center text-[10px] font-bold text-white" style={{ background: "var(--yalola-primary)" }}>{(user?.name || "?")[0]}</span>}
      {user?.name || "Compte supprimé"}
      {role && <span className="rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide" style={{ background: "var(--yalola-surface-alt)", color: "var(--yalola-text-muted)" }}>{ROLE_LABELS[role]}</span>}
    </span>
  );
};

const Line = ({ icon, color, label, user, at, note, testId }) => (
  <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs" data-testid={testId}>
    <span className="inline-flex items-center gap-1 font-bold" style={{ color }}>{icon}{label}</span>
    <span style={{ color: "var(--yalola-text-muted)" }}>par</span>
    <Actor user={user} testId={`${testId}-by`} />
    <span style={{ color: "var(--yalola-text-muted)" }}>le</span>
    <time dateTime={at || ""} className="font-medium" data-testid={`${testId}-at`}>{fmtDateTime(at)}</time>
    {note && <span className="basis-full italic" style={{ color: "var(--yalola-text-muted)" }}>Note : « {note} »</span>}
  </div>
);

// Trace de la décision sur la carte (qui a gardé / masqué / supprimé, quand, note) + suppression définitive (super admin).
export const ModerationDecision = ({ item: it, isSuper, onDeleted }) => {
  const [arm, setArm] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const decided = it.status === "approved" || it.status === "rejected";
  const deletable = isSuper && ["post", "story", "comment", "message", "upload"].includes(it.target_type) && !it.content_deleted && !it.target_deleted;
  if (!decided && !it.quick_hidden && !it.content_deleted && !deletable) return null;

  const del = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/moderation/${it.id}/delete-content`, { note: note.trim() || undefined });
      toast.success(data.deleted ? "Contenu supprimé définitivement" : "Contenu déjà absent — signalement marqué comme supprimé");
      setArm(false); setNote("");
      onDeleted?.(it, data);
    } catch (e) { toast.error(e?.friendlyMessage || "Suppression impossible"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-3 rounded-xl p-2.5 space-y-1.5" style={{ background: "var(--yalola-surface-alt)" }} data-testid={`mod-decision-${it.id}`} data-status={it.content_deleted ? "deleted" : it.status}>
      {it.quick_hidden && it.status === "pending" && (
        <Line icon={<EyeOff size={12} />} color="#B45309" label="Masqué du profil" user={it.quick_hidden_by_user} at={it.quick_hidden_at} testId={`mod-decision-quickhide-${it.id}`} />
      )}
      {decided && (
        it.status === "approved"
          ? <Line icon={<Check size={12} />} color="#15803D" label="Gardé (conforme)" user={it.resolved_by_user} at={it.resolved_at} note={it.note} testId={`mod-decision-kept-${it.id}`} />
          : <Line icon={<EyeOff size={12} />} color="#B91C1C" label="Masqué" user={it.resolved_by_user} at={it.resolved_at} note={it.note} testId={`mod-decision-hidden-${it.id}`} />
      )}
      {it.content_deleted && (
        <Line icon={<Trash2 size={12} />} color="#7F1D1D" label="Supprimé définitivement" user={it.content_deleted_by_user} at={it.content_deleted_at} note={it.content_deleted_note} testId={`mod-decision-deleted-${it.id}`} />
      )}
      {deletable && !arm && (
        <div className="flex items-center justify-between gap-2 pt-1">
          <span className="text-[11px] inline-flex items-center gap-1" style={{ color: "var(--yalola-text-muted)" }}><ShieldCheck size={12} /> Super admin : suppression irréversible du contenu</span>
          <Button size="sm" variant="outline" className="rounded-full h-8" style={{ color: "#7F1D1D", borderColor: "#FCA5A5" }} onClick={() => setArm(true)} data-testid={`mod-delete-${it.id}`}><Trash2 size={14} className="mr-1" /> Supprimer définitivement</Button>
        </div>
      )}
      {deletable && arm && (
        <div className="space-y-2 pt-1" data-testid={`mod-delete-confirm-row-${it.id}`}>
          <p className="text-xs font-semibold" style={{ color: "#7F1D1D" }}>Le contenu sera supprimé pour de bon (irréversible).{it.status === "pending" ? " Le signalement sera clos comme « Masqué » et l'auteur prévenu." : ""}</p>
          <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder="Motif interne (optionnel)…" className="h-9 text-sm" data-testid={`mod-delete-note-${it.id}`} />
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" className="rounded-full" onClick={() => { setArm(false); setNote(""); }} disabled={busy} data-testid={`mod-delete-cancel-${it.id}`}>Annuler</Button>
            <Button size="sm" className="rounded-full text-white" style={{ background: "#7F1D1D" }} onClick={del} disabled={busy} data-testid={`mod-delete-confirm-${it.id}`}>{busy ? <Loader2 size={14} className="animate-spin" /> : <><Trash2 size={14} className="mr-1" /> Confirmer la suppression</>}</Button>
          </div>
        </div>
      )}
    </div>
  );
};
